import { Controller } from "@hotwired/stimulus"
import { icon as brandIcon } from "../lib/icons"

export default class extends Controller {
  static targets = [
    "templateSelect", "templateInfo", "configurationCard", "unitsCard",
    "expenseCard", "expenseFields", "validationCard", "validationResults",
    "previewCard", "previewContent", "actionsCard", "includeUnitsToggle",
    "batchSizeInput", "unitCheckbox", "previewButton", "validateButton", "submitButton"
  ]

  static values = {
    billingPeriodId: Number,
    condoId: Number
  }

  connect() {
    this.selectedTemplate = null
    this.validationPassed = false
    this.previewGenerated = false

    // Initialize form state
    this.updateButtonStates()

    // If template is pre-selected, trigger change
    if (this.templateSelectTarget.value) {
      this.templateChanged()
    }
  }

  templateChanged() {
    const templateId = this.templateSelectTarget.value

    if (!templateId) {
      this.hideAllCards()
      this.updateButtonStates()
      return
    }

    this.selectedTemplate = templateId
    this.validationPassed = false
    this.previewGenerated = false

    // Load template details
    this.loadTemplateDetails(templateId)

    // Show configuration cards
    this.showElement(this.configurationCardTarget)
    this.showElement(this.unitsCardTarget)
    this.showElement(this.actionsCardTarget)

    // Load expense fields for prorated items
    this.loadExpenseFields(templateId)

    this.updateButtonStates()
  }

  loadTemplateDetails(templateId) {
    const url = `/billing_templates/${templateId}`

    fetch(url, {
      headers: {
        'Accept': 'text/vnd.turbo-stream.html',
        'Turbo-Frame': 'template_details'
      }
    })
      .then(response => response.text())
      .then(html => {
        // Update template info section
        if (this.hasTemplateInfoTarget) {
          // Parse the response to extract just the template details
          const parser = new DOMParser()
          const doc = parser.parseFromString(html, 'text/html')
          const templateContent = doc.querySelector('[data-template-details]')

          if (templateContent) {
            this.templateInfoTarget.innerHTML = templateContent.innerHTML
          }

          this.showElement(this.templateInfoTarget)
        }
      })
      .catch(error => {
        console.error('Error loading template details:', error)
      })
  }

  loadExpenseFields(templateId) {
    const params = this.hasBillingPeriodIdValue ? `?billing_period_id=${this.billingPeriodIdValue}` : ''
    const url = `/billing_templates/${templateId}/expense_fields${params}`
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

    fetch(url, {
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken
      }
    })
      .then(response => {
        console.log('Expense fields response status:', response.status)
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }
        return response.json()
      })
      .then(data => {
        console.log('Expense fields data:', data)
        if (data.prorated_items && data.prorated_items.length > 0) {
          this.renderExpenseFields(data.prorated_items)
          this.showElement(this.expenseCardTarget)
        } else {
          console.log('No prorated items found')
          this.hideElement(this.expenseCardTarget)
        }
      })
      .catch(error => {
        console.error('Error loading expense fields:', error)
        this.hideElement(this.expenseCardTarget)
      })
  }

  renderExpenseFields(proratedItems) {
    const esc = (value) => this.escapeHtml(value)
    let fieldsHtml = '<div class="space-y-4">'

    proratedItems.forEach(item => {
      const registered = Number(item.registered_total || 0)
      fieldsHtml += `
        <div class="fieldset">
          <label class="label">
            <span class="font-medium">
              ${esc(item.service_type_name)}
              <span class="text-error">*</span>
            </span>
            <span class="text-xs">${esc(item.calculation_method_display)}</span>
          </label>
          <div class="input-group">
            <span class="bg-base-200">S/</span>
            <input
              type="number"
              name="expense_amounts[item_${Number(item.id)}]"
              step="0.01"
              min="0"
              class="input flex-1"
              placeholder="0.00"
              value="${registered > 0 ? registered.toFixed(2) : ''}"
              data-bulk-invoicing-target="expenseInput"
              data-action="input->bulk-invoicing#expenseAmountChanged"
              required
            />
          </div>
          ${this.registeredExpensesHint(item, registered)}
        </div>
      `
    })

    fieldsHtml += '</div>'
    this.expenseFieldsTarget.innerHTML = fieldsHtml
  }

  // Where the pre-filled amount comes from: bills registered for the item's
  // expense account within the billing period.
  registeredExpensesHint(item, registered) {
    const esc = (value) => this.escapeHtml(value)
    if (!item.expense_account) return ''

    if (registered <= 0) {
      return `
        <div class="label">
          <span class="text-xs text-warning">
            No hay gastos registrados en ${esc(item.expense_account)} para este período.
            <a href="${esc(item.new_expense_url)}" class="link" target="_blank">Registrar gasto</a>
          </span>
        </div>`
    }

    const rows = (item.registered_expenses || []).map(expense => `
      <li><a href="${esc(expense.url)}" class="link link-hover" target="_blank">${esc(expense.date)} · ${esc(expense.supplier)} · ${esc(expense.description)}</a>
        <span class="font-mono">S/ ${Number(expense.amount).toFixed(2)}</span></li>`).join('')
    const more = item.registered_count > (item.registered_expenses || []).length
      ? `<li><a href="${esc(item.expenses_url)}" class="link" target="_blank">Ver todos (${Number(item.registered_count)})</a></li>` : ''

    return `
      <div class="label flex-col items-start">
        <span class="text-xs text-success">
          Monto según ${Number(item.registered_count)} gasto(s) registrado(s) en ${esc(item.expense_account)}. Puede ajustarlo.
        </span>
        <ul class="text-xs list-disc list-inside mt-1">${rows}${more}</ul>
      </div>`
  }

  escapeHtml(value) {
    const div = document.createElement('div')
    div.textContent = value == null ? '' : String(value)
    return div.innerHTML.replace(/"/g, '&quot;')
  }

  expenseAmountChanged(event) {
    const input = event.target
    let value = input.value

    // Store cursor position before formatting
    const cursorPosition = input.selectionStart

    // Format decimal input
    const originalValue = value
    value = value.replace(/[^\d.]/g, '')
    const parts = value.split('.')
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('')
    }
    if (parts[1] && parts[1].length > 2) {
      value = parts[0] + '.' + parts[1].substring(0, 2)
    }

    // Only update value if it actually changed to avoid cursor jumping
    if (originalValue !== value) {
      input.value = value

      // Restore cursor position, adjusting for any removed characters
      const lengthDiff = originalValue.length - value.length
      const newCursorPosition = Math.max(0, cursorPosition - lengthDiff)
      input.setSelectionRange(newCursorPosition, newCursorPosition)
    }

    // Validate positive amount
    const numValue = parseFloat(value)
    if (value && (isNaN(numValue) || numValue <= 0)) {
      input.classList.add('input-error')
    } else {
      input.classList.remove('input-error')
    }

    // Reset validation and preview when amounts change
    this.validationPassed = false
    this.previewGenerated = false
    this.updateButtonStates()
  }

  generatePreview() {
    if (!this.validateForm()) {
      this.showFormValidationErrors()
      return
    }

    this.showElement(this.previewCardTarget)
    this.previewContentTarget.innerHTML = '<div class="loading loading-spinner loading-lg mx-auto"></div>'

    const formData = this.buildFormData()
    const url = `${window.location.pathname.replace('/new', '')}/preview`

    fetch(url, {
      method: 'POST',
      body: formData,
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Accept': 'application/json'
      }
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          this.displayPreview(data.data)
          this.previewGenerated = true
        } else {
          this.showPreviewError(data.message)
        }
        this.updateButtonStates()
      })
      .catch(error => {
        console.error('Error generating preview:', error)
        this.showPreviewError('Error al generar vista previa. Inténtalo de nuevo.')
      })
  }

  validateTemplate() {
    if (!this.selectedTemplate) {
      this.showAlert('error', 'Selecciona una plantilla primero')
      return
    }

    this.showElement(this.validationCardTarget)
    this.validationResultsTarget.innerHTML = '<div class="loading loading-spinner loading-lg mx-auto"></div>'

    const url = `${window.location.pathname.replace('/new', '')}/validate_template?billing_template_id=${this.selectedTemplate}`

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

    fetch(url, {
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken
      }
    })
      .then(response => response.json())
      .then(data => {
        this.displayValidationResults(data)
        this.validationPassed = data.valid
        this.updateButtonStates()
      })
      .catch(error => {
        console.error('Error validating template:', error)
        this.showValidationError('Error al validar plantilla. Inténtalo de nuevo.')
      })
  }

  displayValidationResults(data) {
    let resultsHtml = ''

    if (data.valid) {
      resultsHtml = `
        <div class="alert alert-success">
          ${brandIcon("check", "w-6 h-6")}
          <div>
            <h3 class="font-bold">Validación exitosa</h3>
            <div class="text-xs">La plantilla está correctamente configurada para la generación masiva.</div>
          </div>
        </div>
      `
    } else {
      resultsHtml = `
        <div class="alert alert-error">
          ${brandIcon("exclamation-triangle", "w-6 h-6")}
          <div>
            <h3 class="font-bold">Errores de validación</h3>
            <div class="text-xs">
              <ul class="list-disc list-inside mt-1">
      `

      data.errors.forEach(error => {
        resultsHtml += `<li>${error}</li>`
      })

      resultsHtml += `
              </ul>
            </div>
          </div>
        </div>
      `
    }

    // Add warnings if any
    if (data.warnings && data.warnings.length > 0) {
      resultsHtml += `
        <div class="alert alert-warning mt-2">
          ${brandIcon("exclamation-triangle", "w-6 h-6")}
          <div>
            <h4 class="font-medium">Advertencias</h4>
            <div class="text-xs">
              <ul class="list-disc list-inside mt-1">
      `

      data.warnings.forEach(warning => {
        resultsHtml += `<li>${warning}</li>`
      })

      resultsHtml += `
              </ul>
            </div>
          </div>
        </div>
      `
    }

    this.validationResultsTarget.innerHTML = resultsHtml
  }

  displayPreview(previewData) {
    let previewHtml = `
      <div class="space-y-6">
        <div class="flex justify-between items-center">
          <h4 class="font-semibold">Vista Previa de Generación</h4>
          <div class="flex gap-2">
            <span class="badge badge-outline">${previewData.summary.total_units} unidades</span>
            <span class="badge badge-primary">${previewData.summary.items_count} conceptos</span>
          </div>
        </div>

        <div class="stats shadow">
          <div class="stat">
            <div class="stat-title">Total Estimado</div>
            <div class="stat-value">S/ ${parseFloat(previewData.summary.total_amount || 0).toFixed(2)}</div>
            <div class="stat-desc">Para todas las unidades</div>
          </div>
          <div class="stat">
            <div class="stat-title">Promedio por Unidad</div>
            <div class="stat-value">S/ ${(parseFloat(previewData.summary.total_amount || 0) / (previewData.summary.total_units || 1)).toFixed(2)}</div>
            <div class="stat-desc">Estimación aproximada</div>
          </div>
        </div>

        <div>
          <h5 class="font-medium mb-3">Conceptos a facturar:</h5>
          <div class="space-y-2">
    `

    previewData.items.forEach(item => {
      // Calculate both per-unit and total amounts for all item types
      const totalAmount = parseFloat(item.summary.total_amount || item.summary.total_calculated || 0)
      const unitCount = item.summary.units_count || previewData.summary.total_units || 1
      const perUnitAmount = (totalAmount / unitCount).toFixed(2)
      const formattedTotal = totalAmount.toFixed(2)

      previewHtml += `
        <div class="border rounded p-3">
          <div class="flex justify-between items-center">
            <div class="flex items-center gap-2">
              <span class="badge badge-${this.getItemTypeBadgeColor(item.item_type)}">${item.item_type}</span>
              <span class="font-medium">${item.service_type}</span>
              <span class="text-sm text-base-content/60">${item.calculation_method}</span>
            </div>
            <div class="text-right">
              <div class="font-semibold">S/ ${perUnitAmount} por unidad</div>
              <div class="text-sm text-base-content/60">Total: S/ ${formattedTotal}</div>
            </div>
          </div>
        </div>
        `
    })

    previewHtml += `
          </div>
        </div>

        <div class="alert alert-info">
          ${brandIcon("info", "w-6 h-6")}
          <div>
            <h4 class="font-medium">Vista previa generada</h4>
            <p class="text-sm">Los cálculos mostrados son estimaciones. La generación real puede variar según lecturas de medidores actuales.</p>
          </div>
        </div>
      </div>
    `

    this.previewContentTarget.innerHTML = previewHtml
  }

  buildFormData() {
    const formData = new FormData()

    formData.append('billing_template_id', this.selectedTemplate)
    formData.append('include_units_without_residents', this.includeUnitsToggleTarget.checked ? '1' : '0')

    if (this.batchSizeInputTarget.value) {
      formData.append('batch_size', this.batchSizeInputTarget.value)
    }

    // Add selected unit IDs if any
    const selectedUnits = this.element.querySelectorAll('input[name="unit_ids[]"]:checked')
    selectedUnits.forEach(checkbox => {
      formData.append('unit_ids[]', checkbox.value)
    })

    // Add expense amounts
    const expenseInputs = this.element.querySelectorAll('input[name^="expense_amounts"]')
    expenseInputs.forEach(input => {
      if (input.value) {
        formData.append(input.name, input.value)
      }
    })

    return formData
  }

  validateForm() {
    if (!this.selectedTemplate) {
      return false
    }

    // Validate expense amounts for prorated items
    const expenseInputs = this.element.querySelectorAll('input[name^="expense_amounts"][required]')
    for (let input of expenseInputs) {
      const value = parseFloat(input.value)
      if (!input.value || isNaN(value) || value <= 0) {
        return false
      }
    }

    return true
  }

  showFormValidationErrors() {
    // Add visual feedback for validation errors
    const expenseInputs = this.element.querySelectorAll('input[name^="expense_amounts"][required]')
    let hasErrors = false

    expenseInputs.forEach(input => {
      const value = parseFloat(input.value)
      if (!input.value || isNaN(value) || value <= 0) {
        input.classList.add('input-error')
        hasErrors = true
      } else {
        input.classList.remove('input-error')
      }
    })

    if (hasErrors) {
      this.showAlert('error', 'Completa todos los montos de gastos prorrateados requeridos')
    }
  }

  updateButtonStates() {
    const hasTemplate = !!this.selectedTemplate
    const isFormValid = this.validateForm()

    // Enable validate button if template is selected
    this.validateButtonTarget.disabled = !hasTemplate

    // Enable preview button if form is valid
    this.previewButtonTarget.disabled = !isFormValid

    // Enable submit button if validation passed
    this.submitButtonTarget.disabled = !isFormValid || !this.validationPassed
  }

  showPreviewError(message) {
    this.previewContentTarget.innerHTML = `
      <div class="alert alert-error">
        ${brandIcon("exclamation-triangle", "w-6 h-6")}
        <span>${message}</span>
      </div>
    `
  }

  showValidationError(message) {
    this.validationResultsTarget.innerHTML = `
      <div class="alert alert-error">
        ${brandIcon("exclamation-triangle", "w-6 h-6")}
        <span>${message}</span>
      </div>
    `
  }

  showAlert(type, message) {
    const alertClass = type === 'error' ? 'alert-error' : 'alert-info'
    const alertDiv = document.createElement('div')
    alertDiv.className = `alert ${alertClass} mb-4`
    alertDiv.innerHTML = `
      ${brandIcon("exclamation-triangle", "w-6 h-6")}
      <span>${message}</span>
    `

    this.element.insertBefore(alertDiv, this.element.firstChild)

    setTimeout(() => {
      if (alertDiv.parentNode) {
        alertDiv.remove()
      }
    }, 5000)
  }

  getItemTypeBadgeColor(itemType) {
    switch (itemType) {
      case 'fixed_amount': return 'primary'
      case 'prorated_expense': return 'secondary'
      case 'meter_based': return 'accent'
      default: return 'ghost'
    }
  }

  hideAllCards() {
    if (this.hasTemplateInfoTarget) this.hideElement(this.templateInfoTarget)
    if (this.hasConfigurationCardTarget) this.hideElement(this.configurationCardTarget)
    if (this.hasUnitsCardTarget) this.hideElement(this.unitsCardTarget)
    if (this.hasExpenseCardTarget) this.hideElement(this.expenseCardTarget)
    if (this.hasValidationCardTarget) this.hideElement(this.validationCardTarget)
    if (this.hasPreviewCardTarget) this.hideElement(this.previewCardTarget)
    if (this.hasActionsCardTarget) this.hideElement(this.actionsCardTarget)
  }

  showElement(element) {
    element.classList.remove('hidden')
  }

  hideElement(element) {
    element.classList.add('hidden')
  }
}
